import { useCallback, useEffect, useMemo, useState, type Key } from "react";
import {
  useQueryGetCustomersList,
  useQueryGetCustomersStats,
} from "src/queries/customers.queries";
import type {
  Customer,
  CustomerStatsResponse,
} from "src/services/customerService";
import CustomSharedTitleSection from "../../../sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import "./Customers.css";
import CustomersTable from "./CustomersTable/CustomersTable";
import CreateCustomer from "./components/CreateCustomer";
import { CUSTOMERS_PAGINATION, CUSTOMERS_SORTING } from "./constant";
import { useDebounce } from "../../hooks/useDebounce";
import { useSelectDistributor } from "src/store/selectDistributorStore";

interface CustomersSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

interface CustomersStats {
  all: number;
  active: number;
  inactive: number;
  blocked: number;
}

const buildCustomerColumnSearch = (columnFilters: Record<string, string>) =>
  Object.entries(columnFilters)
    .map(([column, value]) => {
      const normalizedValue = String(value ?? "").trim();
      return normalizedValue ? `${column}=${normalizedValue}` : "";
    })
    .filter(Boolean)
    .join("&");

const getOptionalDistributorId = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const Customers = () => {
  const [pagination, setPagination] = useState(CUSTOMERS_PAGINATION);
  const [sorting, setSorting] = useState<CustomersSorting>(CUSTOMERS_SORTING);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [search, setSearch] = useState("");
  const [customerColumnFilters, setCustomerColumnFilters] = useState<
    Record<string, string>
  >({});
  const [, setSelectedFilters] = useState<Record<string, unknown>>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const { selectedDistributor } = useSelectDistributor();
  const [customerModal, setCustomerModal] = useState<{
    open: boolean;
    editRecord?: Customer | null;
  }>({
    open: false,
    editRecord: null,
  });
  const [stats, setStats] = useState<CustomersStats>({
    all: 0,
    active: 0,
    inactive: 0,
    blocked: 0,
  });
  const debouncedSearch = useDebounce(search, 500);
  const selectedStatus = String(filters.status ?? "ALL").toUpperCase();
  const distributorId = getOptionalDistributorId(selectedDistributor);

  const queryParams = useMemo(() => {
    const columnSearch = buildCustomerColumnSearch(customerColumnFilters);
    const searchParam = debouncedSearch.trim() || columnSearch;

    return {
      size: pagination.size,
      page: pagination.page,
      sortBy: sorting.sortBy ?? "ID",
      sort: sorting.sortDir ?? "DESC",
      status: selectedStatus,
      ...(searchParam ? { search: searchParam } : {}),
      ...(distributorId ? { distributorId } : {}),
    };
  }, [
    customerColumnFilters,
    pagination.page,
    pagination.size,
    debouncedSearch,
    selectedStatus,
    distributorId,
    sorting.sortBy,
    sorting.sortDir,
  ]);

  const {
    data: customersDataApi,
    isLoading,
    refetch,
  } = useQueryGetCustomersList(queryParams, { enabled: true });

  const { data: customersStatsApi, refetch: refetchStats } =
    useQueryGetCustomersStats(
      distributorId ? { distributorId } : {},
      {
        enabled: true,
      },
    );

  const response = customersDataApi?.data?.response;
  const customersData: Customer[] =
    response?.customers ??
    response?.clients ??
    response?.items ??
    response?.data ??
    [];
  const totalCount =
    response?.totalItems ?? response?.total ?? customersData.length;
  const statsResponse = customersStatsApi?.data?.response;

  useEffect(() => {
    const dashboardStats: CustomerStatsResponse | undefined = Array.isArray(
      statsResponse,
    )
      ? statsResponse[0]
      : statsResponse;

    setStats({
      all: Number(
        dashboardStats?.all ??
          dashboardStats?.allCustomers ??
          dashboardStats?.totalCustomers ??
          dashboardStats?.total ??
          0,
      ),
      active: Number(
        dashboardStats?.active ??
          dashboardStats?.activeCustomers ??
          dashboardStats?.ACTIVE ??
          0,
      ),
      inactive: Number(
        dashboardStats?.inactive ??
          dashboardStats?.inactiveCustomers ??
          dashboardStats?.INACTIVE ??
          0,
      ),
      blocked: Number(
        dashboardStats?.blocked ??
          dashboardStats?.blockedCustomers ??
          dashboardStats?.BLOCKED ??
          0,
      ),
    });
  }, [statsResponse]);

  const refetchList = useCallback(async () => {
    await Promise.all([refetch(), refetchStats()]);
    setSelectedRowKeys([]);
  }, [refetch, refetchStats]);

  const handleStatusFilterChange = (status: string) => {
    const nextStatus = status.toUpperCase();

    setFilters((prevFilters) => ({
      ...prevFilters,
      status: nextStatus,
    }));
    setSelectedFilters((prevFilters) => {
      const nextFilters = { ...prevFilters };

      if (nextStatus === "ALL") {
        delete nextFilters.status;
      } else {
        nextFilters.status = nextStatus;
      }

      return nextFilters;
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const applyCustomerColumnSearch = (column: string, value: unknown) => {
    const normalizedValue = String(value ?? "").trim();

    setCustomerColumnFilters((prev) => {
      const nextFilters = { ...prev };

      if (normalizedValue) {
        nextFilters[column] = normalizedValue;
      } else {
        delete nextFilters[column];
      }

      return nextFilters;
    });

    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const resetCustomerFilters = () => {
    setSearch("");
    setCustomerColumnFilters({});
    setFilters({});
    setSelectedFilters({});
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const hasCustomerFilters =
    !!search.trim() ||
    Object.keys(customerColumnFilters).length > 0 ||
    selectedStatus !== "ALL";

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="customers"
        buttonKey="add_customers"
        onClick={() => setCustomerModal({ open: true, editRecord: null })}
        buttonProps={{ className: "!w-[145px] !h-[40px] !text-gray2" }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <CustomersTable
          data={customersData}
          stats={stats}
          activeStatus={selectedStatus}
          onStatusFilterChange={handleStatusFilterChange}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          setSearch={setSearch}
          columnFilters={customerColumnFilters}
          hasFilters={hasCustomerFilters}
          onColumnSearchApply={applyCustomerColumnSearch}
          onResetFilters={resetCustomerFilters}
          selectedRowKeys={selectedRowKeys}
          refetchList={refetchList}
          onEditCustomer={(record) =>
            setCustomerModal({ open: true, editRecord: record })
          }
        />
      </div>
      <CreateCustomer
        open={customerModal.open}
        editRecord={customerModal.editRecord}
        onClose={() => setCustomerModal({ open: false, editRecord: null })}
        refetchList={refetchList}
      />
    </section>
  );
};

export default Customers;
